import {
  minColWidth,
  selectionColWidth,
} from "src/components/ReactiveTable/constants";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { AbstractRow } from "src/components/ReactiveTable/RtTypes";

type UpdateColumnWidthParams<TRow extends AbstractRow> = {
  columns: RtColumn<TRow>[];
  configuredWidths: Record<string, number | string>;
  containerWidth: number;
  hasSelection: boolean;
};

const normalizeWidth = (
  width: number | string | undefined,
  containerWidth: number,
): number => {
  if (typeof width === "number") return width;
  if (!width) return 0;

  const safeWidth = width.trim();
  const numericWidth = parseFloat(safeWidth);
  if (!Number.isFinite(numericWidth)) return 0;

  return safeWidth.endsWith("%")
    ? (numericWidth * containerWidth) / 100
    : numericWidth;
};

export const updateColumnWidth = <TRow extends AbstractRow>({
  columns,
  configuredWidths,
  containerWidth,
  hasSelection,
}: UpdateColumnWidthParams<TRow>): Record<string, number> => {
  const columnWidths: Record<string, number> = {};
  const flexibleColumns: RtColumn<TRow>[] = [];
  let occupiedWidth = hasSelection ? selectionColWidth : 0;

  columns.forEach((column) => {
    const configuredWidth =
      column.capabilities?.resizable === false
        ? 0
        : normalizeWidth(configuredWidths[column.key], containerWidth);
    const declaredWidth = normalizeWidth(column.width, containerWidth);
    const width = configuredWidth || declaredWidth;

    if (!width) {
      flexibleColumns.push(column);
      return;
    }

    const finalWidth = Math.max(width, column.minWidth ?? minColWidth);
    columnWidths[column.key] = finalWidth;
    occupiedWidth += finalWidth;
  });

  const flexibleWidth =
    flexibleColumns.length > 0
      ? Math.max(containerWidth - occupiedWidth, 0) / flexibleColumns.length
      : 0;

  flexibleColumns.forEach((column) => {
    columnWidths[column.key] = Math.max(
      flexibleWidth,
      column.minWidth ?? minColWidth,
    );
  });

  return columnWidths;
};
